import { useState } from 'react'
import { Link } from 'react-router-dom'
import { FlaskConical, Loader2 } from 'lucide-react'
import { PageHeading } from '@/components/layout/PageHeading'
import { LisReviewDialog } from '@/components/lis-review/LisReviewDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useLisReviewList } from '@/hooks/useLisReview'
import type { LisReviewStatus } from '@/api/lisReviewApi'
import { LIS_PENDING_OUTLINE_CLASS, resultStatusLabel } from '@/lib/lisReview'
import { formatRelativeTime } from '@/lib/relativeTime'
import { cn } from '@/lib/utils'

const STATUSES: { value: LisReviewStatus; label: string }[] = [
  { value: 'PENDING', label: 'Waiting for review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'NO_ACTION', label: 'No action' },
]

const PAGE_SIZE = 50

/**
 * Every delivered LIS result by review status, tenant-wide (z8nrz7fcu4).
 *
 * Deliberately not linked from any menu: day to day a result is reached from
 * its notification or the orange badge on its transplant. This is the way to
 * see the whole queue at once, including what was approved, rejected or
 * needed no action. Reached at `/integrations/lis-review`.
 */
export function LisReviewQueuePage() {
  const [status, setStatus] = useState<LisReviewStatus>('PENDING')
  const [mrn, setMrn] = useState('')
  // Cursor of each page visited, so Previous can go back; [''] is page one.
  const [cursors, setCursors] = useState<string[]>([''])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const cursor = cursors[cursors.length - 1]
  const { data, isLoading, isFetching, error } = useLisReviewList({
    status,
    mrn: mrn.trim() || undefined,
    limit: PAGE_SIZE,
    cursor: cursor || undefined,
  })
  const items = data?.items ?? []

  const restart = () => setCursors([''])

  return (
    <div className='p-6'>
      <div className='mb-6 flex items-center gap-3'>
        <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50'>
          <FlaskConical className='h-5 w-5 text-orange-500' />
        </div>
        <PageHeading
          className='flex-1'
          title='LIS result review'
          description='Laboratory results received, and what was decided about each.'
        />
      </div>

      <div className='mb-4 flex flex-wrap items-center gap-3'>
        <div className='inline-flex rounded-lg border border-gray-200 p-0.5'>
          {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => {
                setStatus(s.value)
                restart()
              }}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium',
                status === s.value ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <Input
          className='h-9 w-56'
          placeholder='Filter by MRN'
          value={mrn}
          onChange={(e) => {
            setMrn(e.target.value)
            restart()
          }}
        />
        {isFetching && !isLoading && <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />}
      </div>

      <div className='overflow-x-persistent rounded-xl border border-gray-200 bg-white'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b bg-gray-50 text-left text-xs text-muted-foreground'>
              <th className='px-3 py-2 font-medium'>Test</th>
              <th className='px-3 py-2 font-medium'>MRN</th>
              <th className='px-3 py-2 font-medium'>Order</th>
              <th className='px-3 py-2 font-medium'>Result</th>
              <th className='px-3 py-2 font-medium'>Received</th>
              <th className='px-3 py-2 font-medium'>Values</th>
              <th className='px-3 py-2 font-medium'>Reviewed</th>
              <th className='px-3 py-2 font-medium'>Transplant</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className='py-10 text-center'>
                  <Loader2 className='mx-auto h-5 w-5 animate-spin text-muted-foreground' />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={8} className='py-10 text-center text-destructive'>
                  Could not load the queue: {error instanceof Error ? error.message : 'unknown error'}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className='py-10 text-center text-muted-foreground'>
                  No results here.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => setSelectedId(item.id)}
                  className='cursor-pointer border-b last:border-0 hover:bg-muted/40'
                >
                  <td className='px-3 py-2'>
                    <div className='flex items-center gap-2'>
                      {item.status === 'PENDING' && (
                        <span className='h-2 w-2 shrink-0 rounded-full bg-orange-500' />
                      )}
                      <span className='font-medium'>{item.testCode}</span>
                      {item.testName && <span className='text-muted-foreground'>{item.testName}</span>}
                    </div>
                  </td>
                  <td className='px-3 py-2'>{item.mrn ?? '—'}</td>
                  <td className='px-3 py-2'>{item.orderId ?? '—'}</td>
                  <td className='px-3 py-2'>
                    {resultStatusLabel(item.resultStatus) ?? '—'}
                    {item.notDirectlyOrdered && (
                      <span className='ml-1 text-xs text-muted-foreground'>(not directly ordered)</span>
                    )}
                  </td>
                  <td className='px-3 py-2' title={new Date(item.receivedAt).toLocaleString()}>
                    {formatRelativeTime(item.receivedAt)}
                  </td>
                  <td className='px-3 py-2'>
                    {item.status === 'PENDING' ? (
                      <span
                        className={cn('rounded-full border px-2 py-0.5 text-xs font-medium', LIS_PENDING_OUTLINE_CLASS)}
                      >
                        {item.proposalCount} to write
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className='px-3 py-2 text-xs text-muted-foreground'>
                    {item.reviewedBy ? (
                      <>
                        {item.reviewedBy}
                        {item.reviewedAt && <> · {formatRelativeTime(item.reviewedAt)}</>}
                        {item.reviewNote && <div className='italic'>{item.reviewNote}</div>}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className='px-3 py-2' onClick={(e) => e.stopPropagation()}>
                    {item.mrn && item.procedureId ? (
                      <Link
                        className='text-primary hover:underline'
                        to={`/patients/${encodeURIComponent(item.mrn)}/procedures/${encodeURIComponent(item.procedureId)}`}
                      >
                        Open
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className='mt-3 flex items-center justify-end gap-2'>
        <Button
          variant='outline'
          size='sm'
          disabled={cursors.length === 1}
          onClick={() => setCursors((c) => c.slice(0, -1))}
        >
          Previous
        </Button>
        <Button
          variant='outline'
          size='sm'
          disabled={!data?.nextCursor}
          onClick={() => data?.nextCursor && setCursors((c) => [...c, data.nextCursor as string])}
        >
          Next
        </Button>
      </div>

      <LisReviewDialog resultId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  )
}
